import { useCallback, useEffect, useMemo, useState } from 'react';
import { HistoryIcon } from '../../../util/icons';
import Typography from '@mui/material/Typography';
import Page from '../../../components/Page';
import LocalizedStrings from 'react-localization';
import LOCALIZATIONS from './localizations';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import NFT from '../../../components/NFT';
import MakeAnOffer from '../../../components/MakeAnOffer';
import Grid from '@mui/material/Grid';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { useSnackbar } from 'notistack';
import './style.css';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import { dispatchErr, getTokenHistory } from '../../../util/metamask';
import { utils } from 'web3';
import config from '../../../config';
import CircularProgress from '@mui/material/CircularProgress';
import PageTitle from '../../../components/PageTitle';

const TokenHistory = () => {

    const labels = useMemo(() => new LocalizedStrings( LOCALIZATIONS ), []),
    locale = useSelector( state => state.app.locale ),
    [ loading, setLoading ] = useState( false ),
    [ history, setHistory ] = useState( [] ),
    [ metadata, setMetadata ] = useState( null ),
    [ open, setOpen ] = useState( false ),
    { enqueueSnackbar } = useSnackbar(),
    { id } = useParams();

    /**
     * @name getHistory
     * @function
     * @description Gets the token's history
     */
    const getHistory = useCallback( async () => {
        setLoading( true );
        try {
            const transactions = await getTokenHistory( id );
            setHistory( transactions.map( ({ transactionHash, returnValues }) => ({ transactionHash, amount: returnValues.amount }) ) );
        } catch( err ){
            dispatchErr( err, enqueueSnackbar, labels );
        }
        setLoading( false );
    }, [ enqueueSnackbar, id, labels, setHistory, setLoading ]);

    /**
     * @name toggleOffer
     * @function
     * @description Toggles the open state of the make an offer modal
     * @param {bool} state 
     * @returns 
     */
    const toggleOffer = state => () => {
        setOpen( state );
    }

    // get token history
    useEffect(() => {
        if( id ) getHistory();
    }, [ id, getHistory ]);

    // update locale
    useEffect(() => {
        if( locale !== labels.getLanguage() ) labels.setLanguage( locale )
    }, [ labels, locale ]);

    return (
        <>
            <Page modifier="tokenHistory" title={ labels.title }>
                <PageTitle
                    Icon={ HistoryIcon }
                    title={ labels.title }
                    subtitle={
                        labels.formatString(
                            labels.subtitle,
                            <strong>{ id }</strong>
                        )
                    } />
                <Grid container>
                    <Grid item xs={ 4 }>
                        <NFT id={ Number( id ) } noControls onLoad={ setMetadata } />
                        <Button aria-label={ labels.makeAnOffer } variant="contained" fullWidth onClick={ toggleOffer( true ) }>
                            { labels.makeAnOffer }
                        </Button>
                    </Grid>
                    <Grid item xs={ 8 }>
                        <TableContainer component={ Paper } elevation={ 4 }>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{ labels.transaction }</TableCell>
                                        <TableCell>{ labels.amount }</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {
                                        loading || history.length === 0 ? 
                                            <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                                <TableCell align="center" colSpan={ 2 }>
                                                    {
                                                        loading ?
                                                        <CircularProgress /> :
                                                        <Typography>{ labels.noData }</Typography>
                                                    }
                                                </TableCell>
                                            </TableRow> : 
                                            null
                                    }
                                    {
                                        history.map(
                                            ({ transactionHash, amount }) => (
                                                <TableRow key={ transactionHash } sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                                    <TableCell>
                                                        <Link 
                                                            href={ config.etherscan.replace( '{hash}', transactionHash )}
                                                            target="_blank">
                                                            { transactionHash }
                                                        </Link>
                                                    </TableCell>
                                                    <TableCell>{ utils.fromWei( amount, "ether" ) } ETH</TableCell>
                                                </TableRow>
                                            )
                                        )
                                    }
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Grid>
                </Grid>
            </Page>
            <MakeAnOffer open={ open } metadata={ metadata } onClose={ toggleOffer( false ) } />
        </>
    )

}

export default TokenHistory